// using lowercase class names as String, Number, Boolean are reserved js types

// indicates that the entered string cannot be converted to a value
class ValueValidationError extends Error {

}

// indicates that the entered string cannot be converted to a value but theformat is
// partially correct and can be continued to a valid string
class IntermediateValueError extends Error {

}

// indicates that the entered string cannot be converted to a value but the type is
// providing an auto complete action to get closer to a valid string
class AutocompleteRequest extends Error {

}

function toNullSafeString(value) {
    return value == null ? '' : String(value);
}

// base type class
class Type {

    /**
     * converts a value to the string representation
     * @param {any} value of whatever data type this Type produces
     * @return {string} string representation of the value
    */
    string(value) {
        return value;
    }

    /**
     * converts the string representation to a value of a specific data type
     * @param {string} string the string representation of a value
     * @returns {any} the value in the data type that this Type produces
     */
    value(string) {
        return string;
    }

    /**
     * Tests a value (typed external value not internal string representation)
     * @param {any} value to be validated
     * @returns {boolean} indicating if the value is valid
     */
    validate(value) {
        return true;
    }

    /**
     * DEPRICATED
     * defines the default vee-validate options for this type
     * @returns {array} of vee-validate validator strings
     */
    validators() {
        return [];
    }
}

/**
 * A composite type that wrappes another type
 * if the string representation is '', the value is null,
 * otherwise it acts as the type given in the constructor
 */
class nullable extends Type {
    constructor(type) {
        super();
        this.type = type;
    }
    string(value) {
        return value == null ? '' : this.type.string(value);
    }
    value(string) {
        return string == '' ? null : this.type.value(string);
    }
    validate(value) {
        if(value == null) {
            return true;
        }else {
            return this.type.validate(value);
        }
    }
    validators() {
        return this.type.validators();
    }
}

/**
 * A composite type that wrappes another type
 * if the string representation is '', the value invalid and a validation error is thrown
 * otherwise it acts as the type given in the constructor
 */
class required extends Type {
    constructor(type) {
        super();
        this.type = type;
    }
    string(value) {
        return value == null ? '' : this.type.string(value);
    }
    value(string) {
        if(string == '') {
            throw new ValueValidationError('Required');
        }else {
            return this.type.value(string);
        }
    }
    validate(value) {
        if(value == null) {
            return 'Required';
        }else {
            return this.type.validate(value);
        }
    }
    validators() {
        return this.type.validators();
    }
}

class string extends Type {
    constructor() {
        super();
    }
    string(value) {
        return toNullSafeString(value);
    }
    value(string) {
        return String(string);
    }
}

class int extends Type {
    constructor(args = {}) {
        super();
        this.format_hint = 'Must be a whole number';
        this.min = args.min;
        this.max = args.max;
        this.format = args.format == null || args.format === true;
        this.multiplyers = args.multiplyers || {};
    }
    string(value) {
        if(typeof value == 'number') {
            return this.format ? value.toLocaleString() :  String(value);
        }else{
            return toNullSafeString(value);
        }
    }
    value(string) {

        let regular_expression = new RegExp(`^([\\+-]?[\\d|,]+)([${Object.keys(this.multiplyers).join('')}]?)$`);
        let match = string.match(regular_expression);

        // wrong format all together
        if (!match) throw new ValueValidationError(this.format_hint);

        let [_, integer, multiplyer] = match;

        let clean_integer = integer.replace(/,/g, '');
        if(clean_integer == '-') throw new IntermediateValueError(this.format_hint);

        let value = Number(clean_integer);
        if(multiplyer) value *= this.multiplyers[multiplyer];

        let valid = this.validate(value);
        if(valid === true) return value;
        else throw new ValueValidationError(valid);
    }
    validate(value) {
        if (isNaN(value)) {
            return this.format_hint;
        } else if (this.min != null && value < this.min && this.min == 0) {
            return `Cannot be negative`;
        } else if (this.min != null && value < this.min && this.min == 1) {
            return `Cannot be zero or negative`;
        } else if (this.min != null && value < this.min) {
            return `${this.min} is the minimum`;
        } else if (this.max != null && value > this.max) {
            return `${this.max} is the maximum`;
        } else {
            return true;
        }
    }
}

class decimal extends Type {
    constructor(args = {}) {
        super();
        this.min = args.min;
        this.max = args.max;
        this.decimals = args.decimals;
        this.multiplyers = args.multiplyers || {};
        this.format_hint = 'Must be a number';
    }
    string(value) {
        if(typeof value == 'number') {
            return value.toLocaleString();
        }else{
            return toNullSafeString(value);
        }
    }
    value(string) {

        let regular_expression = new RegExp(`^([\\+-]?[\\d|,]+)(?:\\.([\\d|,]*))?([${Object.keys(this.multiplyers).join('')}]?)$`);
        let match = string.match(regular_expression);

        // wrong format all together
        if (!match) throw new ValueValidationError(this.format_hint);

        let [_, integer, decimal, multiplyer] = match;

        let clean_integer = integer.replace(/,/g, '');
        if(clean_integer == '-') throw new IntermediateValueError(this.format_hint);

        let clean_decimal = decimal == null ? '' : decimal;
        if(this.decimals != null && clean_decimal.length > this.decimals) throw new ValueValidationError(`Maximum ${this.decimals} decimal places`);

        let value = Number(`${clean_integer}.${clean_decimal}`);
        if(multiplyer) value *= this.multiplyers[multiplyer];

        let valid = this.validate(value);
        if(valid === true) return value;
        else throw new ValueValidationError(valid);
    }
    validate(value) {
        if (isNaN(value)) {
            return this.format_hint;
        } else if (this.min != null && value < this.min && this.min == 0) {
            return`Cannot be negative`;
        } else if (this.min != null && value < this.min && this.min == 1) {
            return`Cannot be zero or negative`;
        } else if (this.min != null && value < this.min) {
            return`${this.min} is the minimum`;
        } else if (this.max != null && value > this.max) {
            return`${this.max} is the maximum`;
        } else {
            return true;
        }
    }
}

class date extends Type {
    constructor(args = {}) {
        super();
        this.birth = args.birth;
    }
    string(value) {
        let string = toNullSafeString(value);
        if (value instanceof Date) {
            return '';
        } else if (value === '' || value == null) {
            return '';
        } else if (typeof value == 'string') {

            try {
                let regular_expression = new RegExp(/^(\d{0,4})-(\d{0,2})-(\d{0,2})$/);
                let match = string.match(regular_expression);
                let [_, year, month, day] = match;
                return `${day}/${month}/${year}`;

            } catch (error) {
                console.error('Invalid value supplied to date type', error);
            }

        } else {
            return '';
        }

    }
    value(string, live = true) {
        let format_hint = 'Use the format dd/mm/yyyy';

        try {

            let regular_expression = new RegExp(/^(\d{0,2})(\/|)(\d{0,2})(\/|)(\d{0,4})$/);
            let match = string.match(regular_expression);

            // wrong format all together
            if (!match) throw new ValueValidationError(format_hint);

            let [_, day, sep1, month, sep2, year] = match;

            // for numeric tests
            let int_day = Number(day);
            let int_month = Number(month);
            let int_year = Number(year);

            // if the value does not come from live typing, force string lengths
            if (!live) {
                day = day.padStart(2, '0');
                month = month.padStart(2, '0');
                year = year.padStart(4, '0');
            }

            let is_complete_day = day.length == 2;
            let is_complete_sep1 = sep1.length == 1;
            let is_complete_month = month.length == 2;
            let is_complete_sep2 = sep2.length == 1;
            let is_complete_year = year.length == 4;
            let complete_date = `${year}-${month}-${day}`;
            let date_of_birth = new Date(complete_date);
            let current_date = new Date();

            if (!is_complete_day) throw new IntermediateValueError(format_hint);
            else if (int_day < 1 || int_day > 31) throw new ValueValidationError('Day has to be between 1 and 31');

            if (!is_complete_sep1 && is_complete_day) throw new AutocompleteRequest('/');
            if (!is_complete_sep1) throw new IntermediateValueError(format_hint);

            if (!is_complete_month) throw new IntermediateValueError(format_hint);
            else if (int_month < 1 || int_month > 12) throw new ValueValidationError('Month has to be between 1 and 12');

            if (!is_complete_sep2 && is_complete_month) throw new AutocompleteRequest('/');
            if (!is_complete_sep2) throw new IntermediateValueError(format_hint);

            if (!is_complete_year) throw new IntermediateValueError(format_hint);
            else if (int_year < 1) throw new ValueValidationError(format_hint);

            if(this.birth == true){
                if (date_of_birth.getTime() > current_date.getTime()) throw new ValueValidationError('Enter valid Date of Birth');
            }
            return complete_date;

        } catch (error) {
            if (
                error instanceof ValueValidationError ||
                error instanceof IntermediateValueError ||
                error instanceof AutocompleteRequest
            ) {
                throw error;
            } else {
                console.error(error);
                throw new ValueValidationError(format_hint);
            }
        }
    }
}

// Database limits
const CURRENCY_MAX_ABS_VALUE = 999999999999.99
const INT_MAX_ABS_VALUE = 2147483648

// predefined types
let REQUIRED_STRING = new required(new string());
let REQUIRED_INTEGER = new required(new int({
    min: -INT_MAX_ABS_VALUE,
    max: INT_MAX_ABS_VALUE,
}));
let REQUIRED_POSITIVE_INTEGER = new required(new int({
    min: 0,
    max: INT_MAX_ABS_VALUE,
}));
let REQUIRED_DECIMAL = new required(new decimal({
    min: -CURRENCY_MAX_ABS_VALUE,
    max: CURRENCY_MAX_ABS_VALUE,
}));
let REQUIRED_POSITIVE_DECIMAL = new required(new decimal({
    min: 0,
    max: CURRENCY_MAX_ABS_VALUE,
}));
let REQUIRED_CURRENCY = new required(new decimal({
    min: 0,
    max: CURRENCY_MAX_ABS_VALUE,
    decimals: 2,
    multiplyers: { K: 1000, k: 1000, M: 1000000, m: 1000000 } ,
}));
let REQUIRED_DATE = new required(new date());
let BIRTH_DATE = new required(new date({
    birth: true,
}
));
let EXPIRY_DATE = new nullable(new date());
let REQUIRED_TERM = new required(new int({
    min: 1,
    max: 84,
    multiplyers: {y: 12},
}))
let STRING = new nullable(new string());
let INTEGER = new nullable(new int({
    min: -INT_MAX_ABS_VALUE,
    max: INT_MAX_ABS_VALUE,
}));
let POSITIVE_INTEGER = new nullable(new int({
    min: 0,
    max: INT_MAX_ABS_VALUE,
}));
let DURATION_MONTHS = new nullable(new int({
    min: 0,
    max: 11,
}));
let DURATION_YEARS = new nullable(new int({
    min: 0,
    max: 100,
}));
let YEAR = new nullable(new int({
    min: 1900,
    max: 2099,
    format: false,
}))
let TERM = new nullable(new int({
    min: 1,
    max: 84,
    multiplyers: {y: 12},
}))
let DATE = new nullable(new date());
let CURRENCY = new nullable(new decimal({
    min: 0,
    max: CURRENCY_MAX_ABS_VALUE,
    decimals: 2,
    multiplyers: { K: 1000, k: 1000, M: 1000000, m: 1000000 } ,
}));

let types = {
    nullable,
    required,
    string,
    int,
    date,
    decimal,
    REQUIRED_STRING,
    REQUIRED_DATE,
    BIRTH_DATE,
    EXPIRY_DATE,
    REQUIRED_INTEGER,
    REQUIRED_POSITIVE_INTEGER,
    REQUIRED_DECIMAL,
    REQUIRED_POSITIVE_DECIMAL,
    REQUIRED_CURRENCY,
    REQUIRED_TERM,
    STRING,
    INTEGER,
    POSITIVE_INTEGER,
    DURATION_MONTHS,
    DURATION_YEARS,
    YEAR,
    DATE,
    TERM,
    CURRENCY
};

export {
    ValueValidationError,
    IntermediateValueError,
    AutocompleteRequest,
    types
};